/**
 * The QR a technician scans. The value is the equipment's `qrCodeValue`, an opaque
 * string the companion app resolves against the backend; nothing here needs to know
 * what it means. Rendered as inline SVG so it prints crisp at any size.
 */
import { Download, Printer } from 'lucide-react';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export async function qrSvg(value: string): Promise<string> {
  return QRCode.toString(value, { type: 'svg', errorCorrectionLevel: 'M', margin: 2 });
}

export async function qrPngDataUrl(value: string): Promise<string> {
  return QRCode.toDataURL(value, { errorCorrectionLevel: 'M', margin: 2, width: 1024 });
}

export function QrCode({ value, className }: { value: string; className?: string }) {
  const [svg, setSvg] = useState<string>();
  useEffect(() => {
    let live = true;
    void qrSvg(value).then((s) => {
      if (live) setSvg(s);
    });
    return () => {
      live = false;
    };
  }, [value]);
  if (!svg) return <div className={className} aria-busy="true" />;
  return (
    <div
      className={className}
      role="img"
      aria-label={`QR code for ${value}`}
      data-qr-value={value}
      // Library output, not user markup: QRCode.toString draws only <svg>/<path>.
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

function safeFileName(s: string): string {
  return s.replace(/[^A-Za-z0-9._-]+/g, '_').slice(0, 80) || 'qr';
}

/** Opens a print view: the code, the equipment name and the value, on one sheet. */
async function printQr(value: string, title: string) {
  const svg = await qrSvg(value);
  const w = window.open('', '_blank', 'width=600,height=800');
  if (!w) return;
  const esc = (s: string) =>
    s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
  w.document.write(`<!doctype html><title>${esc(title)}</title>
<style>
  body{margin:0;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;font-family:Inter,system-ui,sans-serif;color:#0B1424}
  .qr{width:min(70vw,480px)} .qr svg{width:100%;height:auto}
  h1{font-size:22px;margin:24px 0 6px;text-align:center} code{font-size:14px;color:#4A5468}
  @media print{ @page{margin:16mm} }
</style>
<div class="qr">${svg}</div><h1>${esc(title)}</h1><code>${esc(value)}</code>
<script>window.onload=function(){window.print()}</script>`);
  w.document.close();
}

export function QrDialog({
  value,
  title,
  open,
  onOpenChange,
}: {
  value: string;
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [png, setPng] = useState<string>();
  useEffect(() => {
    if (!open) return;
    let live = true;
    void qrPngDataUrl(value).then((u) => {
      if (live) setPng(u);
    });
    return () => {
      live = false;
    };
  }, [open, value]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Print this and fix it to the equipment. A technician scans it in the Field IQ app to
            load the procedure.
          </DialogDescription>
        </DialogHeader>
        <QrCode value={value} className="mx-auto w-64 max-w-full rounded-control bg-white p-2" />
        <code className="block break-all text-center text-meta text-ink-dim">{value}</code>
        <div className="flex flex-wrap justify-center gap-2">
          <Button variant="outline" onClick={() => void printQr(value, title)}>
            <Printer /> Print
          </Button>
          <Button variant="outline" asChild disabled={!png}>
            <a href={png} download={`${safeFileName(value)}.png`}>
              <Download /> Download PNG
            </a>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
